/**
 * Owner Dashboard redesign (AB#2206) — strings for the "today" Dashboard.
 * Merged into the runtime dictionary by i18n/owner/index.ts and read through
 * LanguageContext's t(). Every key needs BOTH an en and an ar value.
 * Counts in Arabic use the "label: {count}" pattern (no plural agreement needed).
 */
import type { OwnerStrings } from './types';

export const dashboardStrings: OwnerStrings = {
    en: {
        'odash.eyebrow': 'Today',
        'odash.meta.branches': '{count} branches',
        'odash.meta.branchOne': '1 branch',
        'odash.meta.staff': '{count} staff',
        'odash.meta.updatedNow': 'Updated just now',
        'odash.meta.updatedAgo': 'Updated {wait} ago',
        'odash.branch.label': 'Branch',
        'odash.branch.all': 'All branches',
        'odash.newBooking': 'New booking',
        'odash.newBooking.pickBranch': 'Book at which branch?',

        'odash.attn.region': 'Needs attention',
        'odash.attn.pending.title': 'Requests awaiting confirmation',
        'odash.attn.pending.earliest': 'Earliest: {when}',
        'odash.attn.pending.none': 'No requests waiting',
        'odash.attn.pending.cta': 'Review',
        'odash.attn.unpaid.title': 'Completed, not paid',
        'odash.attn.unpaid.amount': '{amount} to collect',
        'odash.attn.unpaid.none': 'Nothing left to collect',
        'odash.attn.next.title': 'Next: {customer} · {service}',
        'odash.attn.next.with': 'with {staff} · {branch}',
        'odash.attn.next.in': 'in {wait}',
        'odash.attn.next.now': 'starting now',
        'odash.attn.next.none': 'No more appointments today',
        'odash.attn.next.cta': 'Open',
        'odash.attn.allClear': 'All clear. Nothing needs you right now.',
        'odash.attn.allClearSub': 'No requests waiting, nothing to collect and no more appointments today.',

        'odash.today.title': "Today's appointments",
        'odash.today.sub': '{count} bookings · {staff} staff',
        'odash.today.openCalendar': 'Open calendar',
        'odash.today.more': 'See all {count} in Appointments',
        'odash.today.empty': 'No bookings today',
        'odash.today.emptySub': 'New bookings for today will appear here.',
        'odash.today.now': 'Now',
        'odash.today.loadFailed': "Couldn't load today's bookings.",
        'odash.retry': 'Try again',

        'odash.glance.title': 'Today at a glance',
        'odash.glance.bookings': 'Bookings',
        'odash.glance.bookingsSub': '{pending} awaiting · {ended} cancelled or no-show',
        'odash.glance.expected': 'Expected',
        'odash.glance.expectedSub': 'from active bookings',
        'odash.glance.collected': 'Collected',
        'odash.glance.collectedSub': '{pct}% of expected',
        'odash.glance.toCollect': 'To collect',
        'odash.glance.toCollectSub': '{count} completed, unpaid',
        'odash.glance.meter': 'Collected {collected} of {expected}',

        'odash.shift.title': 'On shift today',
        'odash.shift.leave': 'On leave',
        'odash.shift.notWorking': '{count} not working today',
        'odash.shift.moreWorking': '{count} more on shift',
        'odash.shift.none': 'Nobody is scheduled today',
        'odash.shift.load': '{pct}% booked',
        'odash.shift.loadFailed': "Couldn't load today's shifts.",

        'odash.perf.title': 'Performance',
        'odash.perf.scope': 'Figures for the whole salon',
        'odash.perf.period': 'Period',
        'odash.perf.period.7': '7 days',
        'odash.perf.period.30': '30 days',
        'odash.perf.period.month': 'This month',
        'odash.perf.loadFailed': "Couldn't load performance figures.",
        'odash.kpi.revenue': 'Revenue collected',
        'odash.kpi.revenueSub': '{count} payments',
        'odash.kpi.bookings': 'Bookings',
        'odash.kpi.bookingsSub': 'in this period',
        'odash.kpi.completed': 'Completed',
        'odash.kpi.shareSub': 'of bookings in this period',
        'odash.kpi.cancelled': 'Cancelled or no-show',
        'odash.kpi.cancelledWarn': 'Above {pct}% — worth a look',

        'odash.chart.title': 'Bookings and revenue',
        'odash.chart.sub': 'by day',
        'odash.chart.bookings': 'Bookings',
        'odash.chart.revenue': 'Revenue ({currency})',
        'odash.chart.empty': 'No bookings in this period yet',
        'odash.chart.point': '{date}: {bookings} bookings, {revenue}',

        'odash.outcomes.title': 'Booking outcomes',
        'odash.outcomes.sub': '{count} bookings',
        'odash.outcomes.completed': 'Completed',
        'odash.outcomes.confirmed': 'Confirmed, upcoming',
        'odash.outcomes.pending': 'Awaiting confirmation',
        'odash.outcomes.cancelled': 'Cancelled or no-show',

        'odash.pay.title': 'Payments',
        'odash.pay.latest': 'Latest',
        'odash.pay.byMethod': 'By method',
        'odash.pay.all': 'All payments',
        'odash.pay.empty': 'No payments yet',
    },
    ar: {
        'odash.eyebrow': 'اليوم',
        'odash.meta.branches': 'الفروع: {count}',
        'odash.meta.branchOne': 'فرع واحد',
        'odash.meta.staff': 'الموظفون: {count}',
        'odash.meta.updatedNow': 'تم التحديث للتو',
        'odash.meta.updatedAgo': 'آخر تحديث قبل {wait}',
        'odash.branch.label': 'الفرع',
        'odash.branch.all': 'كل الفروع',
        'odash.newBooking': 'حجز جديد',
        'odash.newBooking.pickBranch': 'في أي فرع تريد الحجز؟',

        'odash.attn.region': 'بحاجة إلى انتباهك',
        'odash.attn.pending.title': 'طلبات بانتظار التأكيد',
        'odash.attn.pending.earliest': 'الأقرب: {when}',
        'odash.attn.pending.none': 'لا توجد طلبات بانتظارك',
        'odash.attn.pending.cta': 'مراجعة',
        'odash.attn.unpaid.title': 'مكتملة وغير مدفوعة',
        'odash.attn.unpaid.amount': 'للتحصيل: {amount}',
        'odash.attn.unpaid.none': 'لا مبالغ بانتظار التحصيل',
        'odash.attn.next.title': 'التالي: {customer} · {service}',
        'odash.attn.next.with': 'مع {staff} · {branch}',
        'odash.attn.next.in': 'بعد {wait}',
        'odash.attn.next.now': 'يبدأ الآن',
        'odash.attn.next.none': 'لا مواعيد أخرى اليوم',
        'odash.attn.next.cta': 'فتح',
        'odash.attn.allClear': 'كل شيء على ما يرام. لا شيء يحتاج إلى متابعتك الآن.',
        'odash.attn.allClearSub': 'لا طلبات بانتظارك، ولا مبالغ للتحصيل، ولا مواعيد أخرى اليوم.',

        'odash.today.title': 'مواعيد اليوم',
        'odash.today.sub': 'الحجوزات: {count} · الموظفون: {staff}',
        'odash.today.openCalendar': 'فتح التقويم',
        'odash.today.more': 'عرض الكل ({count}) في المواعيد',
        'odash.today.empty': 'لا حجوزات اليوم',
        'odash.today.emptySub': 'ستظهر هنا حجوزات اليوم الجديدة.',
        'odash.today.now': 'الآن',
        'odash.today.loadFailed': 'تعذّر تحميل حجوزات اليوم.',
        'odash.retry': 'إعادة المحاولة',

        'odash.glance.title': 'ملخص اليوم',
        'odash.glance.bookings': 'الحجوزات',
        'odash.glance.bookingsSub': 'بانتظار التأكيد: {pending} · ملغاة أو لم يحضر: {ended}',
        'odash.glance.expected': 'المتوقع',
        'odash.glance.expectedSub': 'من الحجوزات القائمة',
        'odash.glance.collected': 'المحصّل',
        'odash.glance.collectedSub': '{pct}% من المتوقع',
        'odash.glance.toCollect': 'للتحصيل',
        'odash.glance.toCollectSub': 'مكتملة وغير مدفوعة: {count}',
        'odash.glance.meter': 'تم تحصيل {collected} من {expected}',

        'odash.shift.title': 'في الدوام اليوم',
        'odash.shift.leave': 'في إجازة',
        'odash.shift.notWorking': 'لا يعملون اليوم: {count}',
        'odash.shift.moreWorking': 'آخرون في الدوام: {count}',
        'odash.shift.none': 'لا أحد في جدول العمل اليوم',
        'odash.shift.load': 'نسبة الحجز {pct}%',
        'odash.shift.loadFailed': 'تعذّر تحميل مناوبات اليوم.',

        'odash.perf.title': 'الأداء',
        'odash.perf.scope': 'الأرقام لكامل الصالون',
        'odash.perf.period': 'الفترة',
        'odash.perf.period.7': '7 أيام',
        'odash.perf.period.30': '30 يومًا',
        'odash.perf.period.month': 'هذا الشهر',
        'odash.perf.loadFailed': 'تعذّر تحميل أرقام الأداء.',
        'odash.kpi.revenue': 'الإيرادات المحصّلة',
        'odash.kpi.revenueSub': 'عدد الدفعات: {count}',
        'odash.kpi.bookings': 'الحجوزات',
        'odash.kpi.bookingsSub': 'خلال هذه الفترة',
        'odash.kpi.completed': 'مكتملة',
        'odash.kpi.shareSub': 'من حجوزات هذه الفترة',
        'odash.kpi.cancelled': 'ملغاة أو لم يحضر العميل',
        'odash.kpi.cancelledWarn': 'أعلى من {pct}% — يُنصح بالمراجعة',

        'odash.chart.title': 'الحجوزات والإيرادات',
        'odash.chart.sub': 'حسب اليوم',
        'odash.chart.bookings': 'الحجوزات',
        'odash.chart.revenue': 'الإيرادات ({currency})',
        'odash.chart.empty': 'لا حجوزات في هذه الفترة بعد',
        'odash.chart.point': '{date}: الحجوزات {bookings}، الإيرادات {revenue}',

        'odash.outcomes.title': 'نتائج الحجوزات',
        'odash.outcomes.sub': 'الحجوزات: {count}',
        'odash.outcomes.completed': 'مكتملة',
        'odash.outcomes.confirmed': 'مؤكدة وقادمة',
        'odash.outcomes.pending': 'بانتظار التأكيد',
        'odash.outcomes.cancelled': 'ملغاة أو لم يحضر العميل',

        'odash.pay.title': 'المدفوعات',
        'odash.pay.latest': 'الأحدث',
        'odash.pay.byMethod': 'حسب الطريقة',
        'odash.pay.all': 'كل المدفوعات',
        'odash.pay.empty': 'لا مدفوعات بعد',
    },
};
